import type { EurotaxBackfillVehicle } from '../types';

type Translate = (key: string, fallback: string) => string;

export interface MigrationDebug {
	codice_costruttore_raw?: string;
	manufacture_code_used?: string;
	outcome?: string;
	retry_count?: number;
	vehicle_kinds_tried?: string[];
	errors_by_kind?: Record<string, string>;
	message?: string;
	processed_at?: string;
}

export type VehicleStatusKey =
	| 'neverProcessed'
	| 'resolved'
	| 'manual'
	| 'ambiguous'
	| 'priceMismatch'
	| 'skipped'
	| 'notFound';

export interface VehicleStatusMeta {
	labelKey: string;
	fallback: string;
	bg: 'danger' | 'warning' | 'success' | 'secondary';
	text?: 'dark';
}

export const VEHICLE_STATUS_META: Record<VehicleStatusKey, VehicleStatusMeta> =
	{
		neverProcessed: {
			labelKey: 'motornet_backfill_filter_never_processed',
			fallback: 'Mai processato',
			bg: 'secondary',
		},
		resolved: {
			labelKey: 'motornet_backfill_resolved',
			fallback: 'Risolto',
			bg: 'success',
		},
		manual: {
			labelKey: 'motornet_backfill_filter_manual',
			fallback: 'Manuale',
			bg: 'success',
		},
		ambiguous: {
			labelKey: 'motornet_backfill_ambiguous',
			fallback: 'Ambiguo',
			bg: 'warning',
			text: 'dark',
		},
		priceMismatch: {
			labelKey: 'motornet_backfill_filter_price_mismatch',
			fallback: 'Prezzo divergente',
			bg: 'warning',
			text: 'dark',
		},
		skipped: {
			labelKey: 'motornet_backfill_filter_skipped',
			fallback: 'Saltato',
			bg: 'warning',
			text: 'dark',
		},
		notFound: {
			labelKey: 'motornet_backfill_not_found',
			fallback: 'Non trovato',
			bg: 'danger',
		},
	};

export function migrationDebugOf(
	vehicle: EurotaxBackfillVehicle
): MigrationDebug | null {
	return (vehicle.migration_debug as MigrationDebug | null) ?? null;
}

export function isNeverProcessed(vehicle: EurotaxBackfillVehicle): boolean {
	const debug = migrationDebugOf(vehicle);
	return debug ? debug.outcome === 'neverProcessed' : true;
}

export function isRetryable(vehicle: EurotaxBackfillVehicle): boolean {
	const outcome = migrationDebugOf(vehicle)?.outcome;
	return (
		outcome === 'skipped' || outcome === 'notFound' || isNeverProcessed(vehicle)
	);
}

export function statusKeyOf(vehicle: EurotaxBackfillVehicle): VehicleStatusKey {
	if (isNeverProcessed(vehicle)) return 'neverProcessed';

	const outcome = migrationDebugOf(vehicle)?.outcome;
	if (outcome && outcome in VEHICLE_STATUS_META) {
		return outcome as VehicleStatusKey;
	}

	if (vehicle.not_found) return 'notFound';
	if (vehicle.ambiguous) return 'ambiguous';
	if (vehicle.price_mismatch) return 'priceMismatch';
	return 'resolved';
}

export function isResolvable(vehicle: EurotaxBackfillVehicle): boolean {
	if (vehicle.ambiguous || vehicle.not_found) return true;
	return statusKeyOf(vehicle) === 'priceMismatch';
}

export function statusLabelOf(
	vehicle: EurotaxBackfillVehicle,
	t: Translate
): string {
	const { labelKey, fallback } = VEHICLE_STATUS_META[statusKeyOf(vehicle)];
	return t(labelKey, fallback);
}
